﻿import type { SipClient, InitOptions, PbxConfig, Events } from "../api";

// tiny event helper
function emit<E extends keyof Events>(
    ev: E,
    bus?: Partial<Events>,
    payload?: Parameters<NonNullable<Events[E]>>[0]
) {
    const h = bus?.[ev] as any;
    try { h?.(payload); }
    catch (err) { console.error("ETS.SipClient handler error", err); }
}
export class NoopClient implements SipClient {
    private events?: Partial<Events>;
    private audioId?: string;
    private callRemotes = new Map<string, string>();
    private callStates = new Map<string, "establishing" | "active" | "held">();
    private activeCallId?: string;

    private holdOtherActiveCalls(nextCallId: string): void {
        for (const [callId, state] of this.callStates.entries()) {
            if (callId === nextCallId || state !== "active") continue;
            this.callStates.set(callId, "held");
            emit("callUpdated", this.events, {
                callId,
                remoteUri: this.callRemotes.get(callId) ?? "",
                state: "held",
                isActive: false
            });
        }

        if (this.activeCallId !== nextCallId) {
            this.activeCallId = undefined;
        }
    }

    init(opts?: InitOptions): void {
        this.events = opts?.events;
        this.audioId = opts?.audioElementId;
        console.info("[ETS] SipClient.init", { audioId: this.audioId });
        emit("transportChanged", this.events, { state: "disconnected" });
        emit("registrationChanged", this.events, { state: "unregistered" });
    }

    async connect(cfg: PbxConfig, opt?: { register?: boolean }): Promise<void> {
        console.info("[ETS] connect()", { cfg: { ...cfg, password: "***" }, opt });
        emit("transportChanged", this.events, { state: "connected" });
    }

    async disconnect(): Promise<void> {
        console.info("[ETS] disconnect()");
        this.callRemotes.clear();
        this.callStates.clear();
        this.activeCallId = undefined;
        emit("registrationChanged", this.events, { state: "unregistered" });
        emit("transportChanged", this.events, { state: "disconnected" });
    }
    async watchBlf(extensions: string[]): Promise<boolean> {
        const clean = Array.from(
            new Set(
                (extensions || [])
                    .map(x => String(x || "").trim())
                    .filter(x => x.length > 0)
            )
        );

        for (const ext of clean) {
            this.events?.blfChanged?.({
                extension: ext,
                blfState: "Unknown",
                presenceState: "Unknown",
                eventType: "mock"
            });
        }

        return true;
    }
    async dial(targetUri: string): Promise<string> {
        const callId = (globalThis.crypto as any)?.randomUUID?.() ?? Math.random().toString(36).slice(2);
        console.info("[ETS] dial()", { targetUri, callId });
        this.holdOtherActiveCalls(callId);
        this.callRemotes.set(callId, targetUri);
        this.callStates.set(callId, "establishing");
        this.activeCallId = callId;
        emit("callUpdated", this.events, { callId, remoteUri: targetUri, state: "establishing", isActive: true });
        this.callStates.set(callId, "active");
        emit("callUpdated", this.events, { callId, remoteUri: targetUri, state: "active", isActive: true });
        return callId;
    }

    async answer(callId: string): Promise<boolean> {
        console.info("[ETS] answer()", { callId });
        this.holdOtherActiveCalls(callId);
        this.callStates.set(callId, "active");
        this.activeCallId = callId;
        emit("callUpdated", this.events, { callId, remoteUri: "", state: "active", isActive: true });
        return true;
    }
    async decline(callId: string): Promise<boolean> {
        console.info("[ETS] decline()", { callId });
        emit("callUpdated", this.events, { callId, remoteUri: "", state: "ended", isActive: false });
        this.callStates.delete(callId);
        if (this.activeCallId === callId) this.activeCallId = undefined;
        return true;
    }
    async hangup(callId: string): Promise<boolean> {
        console.info("[ETS] hangup()", { callId });
        emit("callUpdated", this.events, { callId, remoteUri: this.callRemotes.get(callId) ?? "", state: "ended", isActive: false });
        this.callRemotes.delete(callId);
        this.callStates.delete(callId);
        if (this.activeCallId === callId) this.activeCallId = undefined;
        return true;
    }
    async refreshVoicemail(): Promise<boolean> {
        return true;
    }
    async hold(callId: string): Promise<boolean> {
        console.info("[ETS] hold()", { callId });
        this.callStates.set(callId, "held");
        if (this.activeCallId === callId) this.activeCallId = undefined;
        emit("callUpdated", this.events, { callId, remoteUri: this.callRemotes.get(callId) ?? "", state: "held", isActive: false });
        return true;
    }
    async resume(callId: string): Promise<boolean> {
        console.info("[ETS] resume()", { callId });
        this.holdOtherActiveCalls(callId);
        this.callStates.set(callId, "active");
        this.activeCallId = callId;
        emit("callUpdated", this.events, { callId, remoteUri: this.callRemotes.get(callId) ?? "", state: "active", isActive: true });
        return true;
    }
    async makeActive(callId: string): Promise<boolean> {
        console.info("[ETS] makeActive()", { callId });
        this.holdOtherActiveCalls(callId);
        this.callStates.set(callId, "active");
        this.activeCallId = callId;
        emit("callUpdated", this.events, { callId, remoteUri: this.callRemotes.get(callId) ?? "", state: "active", isActive: true });
        return true;
    }

    async setMicrophoneMuted(callId: string, muted: boolean): Promise<boolean> {
        console.info("[ETS] setMicrophoneMuted() [mock]", { callId, muted });
        return true;
    }

    async register(): Promise<void> {
        console.info("[ETS] register() [mock]");
        emit("registrationChanged", this.events, {
            state: "registered",
            message: "Mock registration successful"
        });
    }

    async unregister(): Promise<void> {
        console.info("[ETS] unregister() [mock]");
        emit("registrationChanged", this.events, {
            state: "unregistered",
            message: "Mock unregistered"
        });
    }
    async sendDtmf(_callId: string, _tones: string, _durationMs = 200, _gapMs = 70): Promise<boolean> {
        console.info("[ETS] NoopClient.sendDtmf (mock)", { _callId, _tones });
        return true;
    }

    async blindTransfer(callId: string, targetUri: string): Promise<boolean> {
        console.info("[ETS] blindTransfer() [mock]", { callId, targetUri });
        emit("callUpdated", this.events, { callId, remoteUri: this.callRemotes.get(callId) ?? targetUri, state: "ended", isActive: false });
        this.callRemotes.delete(callId);
        return true;
    }

    async startAttendedTransfer(sourceCallId: string, targetUri: string): Promise<{ sourceCallId: string; consultCallId: string } | null> {
        console.info("[ETS] startAttendedTransfer() [mock]", { sourceCallId, targetUri });
        await this.hold(sourceCallId);
        const consultCallId = await this.dial(targetUri);
        return { sourceCallId, consultCallId };
    }

    async completeAttendedTransfer(sourceCallId: string, consultCallId: string): Promise<boolean> {
        console.info("[ETS] completeAttendedTransfer() [mock]", { sourceCallId, consultCallId });
        await this.hangup(consultCallId);
        await this.hangup(sourceCallId);
        return true;
    }

    async cancelAttendedTransfer(sourceCallId: string, consultCallId: string): Promise<boolean> {
        console.info("[ETS] cancelAttendedTransfer() [mock]", { sourceCallId, consultCallId });
        await this.hangup(consultCallId);
        await this.resume(sourceCallId);
        return true;
    }


}
